import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AVAILABILITY_LABELS,
  ILiveChatConversation,
  ILiveChatConversationSummary,
  ILiveChatDashboard,
  ILiveChatMessage,
  ILiveChatQuickReply,
  LiveChatAgentAvailability,
  PRIORITY_COLORS,
  PRIORITY_LABELS,
  STATUS_LABELS,
  TOPIC_LABELS,
  normalizeConversation,
  normalizeMessage,
  parseOrderCard,
} from "../models/ILiveChat";
import {
  claimLiveChat,
  closeLiveChatAsAgent,
  getAgentConversation,
  getAgentQuickReplies,
  getLiveChatDashboard,
  getLiveChatInbox,
  getLiveChatQueue,
  markAgentConversationRead,
  resolveLiveChat,
  sendLiveChatMessage,
  setAgentAvailability,
  consumeQuickReply,
} from "../services/liveChatAgent";
import { useLiveChatAgentHub } from "../hooks/useLiveChatAgentHub";
import { getUserToken } from "../../utils/Util";
import { OrderDetailModal } from "../components/OrderDetailModal";
import { AgentToolsModal } from "../components/AgentToolsModal";
import { OnCallAgentModal } from "../components/OnCallAgentModal";
import "../live-chat.css";

type ListTab = "queue" | "mine" | "all";

const newClientMessageId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });

const formatTimeAgo = (iso: string): string => {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "Az önce";
  if (diff < 3600) return `${Math.floor(diff / 60)} dk`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} sa`;
  return new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "short" });
};

const formatSeconds = (seconds?: number | null) => {
  if (seconds == null) return "—";
  if (seconds < 60) return `${Math.round(seconds)} sn`;
  return `${Math.round(seconds / 60)} dk`;
};

const getInitials = (name?: string | null) =>
  (name || "M")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

/* ── Alt bileşenler ──────────────────────────────────────── */

function StatCard({ label, value, hint, accent = false }: {
  label: string; value: string; hint?: string; accent?: boolean;
}) {
  return (
    <div style={{
      flex: "1 1 110px",
      background: accent ? "linear-gradient(135deg, #6366f1, #4f46e5)" : "#fff",
      border: accent ? "none" : "1.5px solid #eef2ff",
      borderRadius: 12,
      padding: "10px 14px",
      boxShadow: accent ? "0 4px 14px rgba(99,102,241,0.25)" : "none",
    }}>
      <div style={{ fontSize: "0.68rem", color: accent ? "rgba(255,255,255,0.8)" : "#6b7280", fontWeight: 600, marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: "1.3rem", fontWeight: 800, color: accent ? "#fff" : "#111827", lineHeight: 1.2 }}>
        {value}
      </div>
      {hint && <div style={{ fontSize: "0.63rem", color: accent ? "rgba(255,255,255,0.65)" : "#9ca3af", marginTop: 1 }}>{hint}</div>}
    </div>
  );
}

function ConversationRow({ item, active, onClick }: {
  item: ILiveChatConversationSummary;
  active: boolean;
  onClick: () => void;
}) {
  const initials = getInitials(item.customerName);
  const timeStr = item.lastMessageAt ? formatTimeAgo(item.lastMessageAt) : "";

  return (
    <button
      onClick={onClick}
      className="lc-conv-row"
      style={{
        width: "100%",
        textAlign: "left",
        border: "none",
        borderBottom: "1px solid #f1f5f9",
        borderLeft: `3px solid ${PRIORITY_COLORS[item.priority]}`,
        background: active ? "#eef2ff" : "#fff",
        padding: "10px 14px",
        cursor: "pointer",
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
      }}
    >
      {/* Avatar */}
      <div style={{
        width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
        background: active ? "#6366f1" : "#e0e7ff",
        color: active ? "#fff" : "#4f46e5",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "0.7rem", fontWeight: 800,
      }}>
        {initials}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 4 }}>
          <span style={{ fontWeight: 700, fontSize: "0.82rem", color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {item.customerName || "Müşteri"}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
            {timeStr && <span style={{ fontSize: "0.62rem", color: "#9ca3af" }}>{timeStr}</span>}
            {item.unreadCount > 0 && (
              <span style={{
                background: "#dc2626", color: "#fff", borderRadius: 10,
                padding: "1px 6px", fontSize: "0.62rem", fontWeight: 800, lineHeight: 1.6,
              }}>
                {item.unreadCount}
              </span>
            )}
          </div>
        </div>

        <div style={{
          fontSize: "0.74rem", color: "#6b7280",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          margin: "2px 0",
          fontWeight: item.unreadCount > 0 ? 600 : 400,
        }}>
          {item.lastMessagePreview || "—"}
        </div>

        <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
          <span style={{ fontSize: "0.6rem", fontWeight: 700, color: PRIORITY_COLORS[item.priority] }}>
            {PRIORITY_LABELS[item.priority]}
          </span>
          <span style={{ fontSize: "0.6rem", color: "#d1d5db" }}>·</span>
          <span style={{ fontSize: "0.6rem", color: "#9ca3af" }}>{TOPIC_LABELS[item.topic]}</span>
          {item.waitingMinutes != null && (
            <>
              <span style={{ fontSize: "0.6rem", color: "#d1d5db" }}>·</span>
              <span style={{ fontSize: "0.6rem", color: "#f59e0b", fontWeight: 700 }}>
                <i className="bi bi-clock me-1" />{item.waitingMinutes} dk
              </span>
            </>
          )}
        </div>
      </div>
    </button>
  );
}

function Bubble({ message, myUserId, onViewOrder }: {
  message: ILiveChatMessage;
  myUserId: string | null;
  onViewOrder?: (orderNo: string) => void;
}) {
  if (message.messageType === "System") {
    return (
      <div style={{ alignSelf: "center", maxWidth: "85%" }}>
        <div style={{
          background: "#f1f5f9", color: "#6b7280", borderRadius: 20,
          padding: "4px 14px", fontSize: "0.7rem", textAlign: "center",
          border: "1px solid #e2e8f0",
        }}>
          {message.content}
        </div>
      </div>
    );
  }

  if (message.messageType === "OrderCard") {
    const order = parseOrderCard(message.metadataJson);
    return (
      <div style={{
        alignSelf: "center", width: "92%",
        background: "#fff", border: "1px solid #e0e7ff",
        borderLeft: "3px solid #6366f1", borderRadius: 12, padding: "10px 14px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}>
        <div style={{ fontWeight: 700, fontSize: "0.78rem", color: "#4f46e5", marginBottom: 4 }}>
          <i className="bi bi-receipt me-1" />
          {order?.orderNo ? `#${order.orderNo}` : "Sipariş"}
          {order?.restaurantName && (
            <span style={{ color: "#6b7280", fontWeight: 400, marginLeft: 6 }}>— {order.restaurantName}</span>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "0.76rem", color: "#374151" }}>
          <span>{order?.statusName ?? message.content}</span>
          {order && <strong style={{ color: "#111827" }}>{order.orderPrice.toFixed(2)}₺</strong>}
        </div>
        {order?.orderNo && onViewOrder && (
          <button
            onClick={() => onViewOrder(order!.orderNo!)}
            style={{
              marginTop: 8, width: "100%",
              background: "linear-gradient(135deg, #6366f1, #4f46e5)",
              border: "none", borderRadius: 8,
              color: "#fff", fontWeight: 700, fontSize: "0.75rem",
              padding: "6px 0", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
            }}
          >
            <i className="bi bi-eye" /> Siparişi Görüntüle
          </button>
        )}
      </div>
    );
  }

  const isMine = message.senderId
    ? message.senderId === myUserId
    : message.senderRole !== "Customer";
  const isPending = message.id.startsWith("pending-");

  return (
    <div style={{ display: "flex", justifyContent: isMine ? "flex-end" : "flex-start", alignItems: "flex-end", gap: 6 }}>
      {!isMine && (
        <div style={{
          width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
          background: message.senderRole === "Bot" ? "#fef3c7" : "#e0e7ff",
          color: message.senderRole === "Bot" ? "#92400e" : "#4f46e5",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "0.65rem", fontWeight: 800,
        }}>
          {message.senderRole === "Bot" ? "🤖" : getInitials(message.senderName)}
        </div>
      )}
      <div style={{
        maxWidth: "72%",
        background: isMine ? "linear-gradient(135deg, #6366f1, #4f46e5)" : "#fff",
        color: isMine ? "#fff" : "#111827",
        border: isMine ? "none" : "1.5px solid #e5e7eb",
        borderRadius: isMine ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
        padding: "9px 13px",
        opacity: isPending ? 0.6 : 1,
        boxShadow: isMine ? "0 2px 10px rgba(99,102,241,0.3)" : "0 1px 4px rgba(0,0,0,0.06)",
      }}>
        {!isMine && message.senderName && (
          <div style={{ fontSize: "0.64rem", fontWeight: 700, color: "#6366f1", marginBottom: 3 }}>
            {message.senderRole === "Bot" ? "Asistan" : message.senderName}
          </div>
        )}
        {message.messageType === "Image" && message.attachmentUrl && (
          <a href={message.attachmentUrl} target="_blank" rel="noopener noreferrer">
            <img
              src={message.attachmentUrl}
              alt={message.attachmentName ?? ""}
              style={{ maxWidth: "100%", borderRadius: 8, marginBottom: 5, display: "block" }}
            />
          </a>
        )}
        <div style={{ fontSize: "0.84rem", whiteSpace: "pre-wrap", lineHeight: 1.45 }}>
          {message.content}
        </div>
        <div style={{ fontSize: "0.62rem", opacity: 0.65, textAlign: "right", marginTop: 3 }}>
          {formatTime(message.sentAt)}
          {isPending && " · Gönderiliyor..."}
        </div>
      </div>
    </div>
  );
}

/* ── Ana sayfa ───────────────────────────────────────────── */

function LiveChatAgentPage() {
  const token = getUserToken();
  const myUserId = token?.userId ?? null;
  const isLiveChatOnly = !!(token as any)?.isLiveChatOnly;
  const navigate = useNavigate();
  const handleLogout = () => {
    localStorage.removeItem("user");
    navigate("/", { replace: true });
  };

  const [availability, setAvailability] = useState<LiveChatAgentAvailability>("Offline");
  const [maxChats, setMaxChats] = useState(5);
  const [dashboard, setDashboard] = useState<ILiveChatDashboard | null>(null);

  const [tab, setTab] = useState<ListTab>("queue");
  const [list, setList] = useState<ILiveChatConversationSummary[]>([]);
  const [listLoading, setListLoading] = useState(false);

  const [conversation, setConversation] = useState<ILiveChatConversation | null>(null);
  const [messages, setMessages] = useState<ILiveChatMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);

  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [macros, setMacros] = useState<ILiveChatQuickReply[]>([]);
  const [showMacros, setShowMacros] = useState(false);
  const [error, setError] = useState("");

  const [showClosedModal, setShowClosedModal] = useState(false);
  const [closedList, setClosedList] = useState<ILiveChatConversationSummary[]>([]);
  const [closedLoading, setClosedLoading] = useState(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileView, setMobileView] = useState<"list" | "chat">("list");

  const [orderDetailNo, setOrderDetailNo] = useState<string | null>(null);
  const [showToolsModal, setShowToolsModal] = useState(false);
  const [showOnCallModal, setShowOnCallModal] = useState(false);

  const conversationIdRef = useRef<string | null>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    conversationIdRef.current = conversation?.id ?? null;
  }, [conversation]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* ── Veri yükleme ── */

  const loadDashboard = useCallback(() => {
    getLiveChatDashboard()
      .then((res) => {
        setDashboard(res.data);
        const me = res.data.agents?.find((a) => a.agentId === myUserId);
        if (me) {
          setAvailability(me.availability);
          setMaxChats(me.maxConcurrentChats);
        }
      })
      .catch(() => {});
  }, [myUserId]);

  const loadList = useCallback((which: ListTab) => {
    setListLoading(true);
    const request =
      which === "queue"
        ? getLiveChatQueue()
        : getLiveChatInbox({ onlyMine: which === "mine", pageSize: 50 });

    request
      .then((res) => setList((res.data ?? []).filter((s) => s.status !== "Closed")))
      .catch(() => setList([]))
      .finally(() => setListLoading(false));
  }, []);

  const openClosedModal = () => {
    setShowClosedModal(true);
    setClosedLoading(true);
    getLiveChatInbox({ status: "Closed", pageSize: 50 })
      .then((res) => setClosedList(res.data ?? []))
      .catch(() => setClosedList([]))
      .finally(() => setClosedLoading(false));
  };

  const openConversation = useCallback((conversationId: string) => {
    setThreadLoading(true);
    setError("");
    setShowMacros(false);
    getAgentConversation(conversationId)
      .then((res) => {
        const normalized = normalizeConversation(res.data);
        setConversation(normalized);
        setMessages(normalized.messages);
        if (normalized.unreadCount > 0) {
          markAgentConversationRead(conversationId).catch(() => {});
        }
        setTimeout(() => textareaRef.current?.focus(), 100);
      })
      .catch(() => setError("Sohbet yüklenemedi."))
      .finally(() => setThreadLoading(false));
  }, []);

  useEffect(() => {
    loadDashboard();
    getAgentQuickReplies()
      .then((res) => setMacros(res.data ?? []))
      .catch(() => {});
  }, [loadDashboard]);

  useEffect(() => { loadList(tab); }, [tab, loadList]);

  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  /* ── SignalR ── */

  const { joinConversation } = useLiveChatAgentHub({
    token: token?.token ?? null,
    onMessage: (message) => {
      if (message.conversationId === conversationIdRef.current) {
        setMessages((prev) => {
          const byId = prev.findIndex((m) => m.id === message.id);
          if (byId !== -1) {
            const next = [...prev];
            next[byId] = message;
            return next;
          }
          if (message.clientMessageId) {
            const byClient = prev.findIndex((m) => m.clientMessageId === message.clientMessageId);
            if (byClient !== -1) {
              const next = [...prev];
              next[byClient] = message;
              return next;
            }
          }
          return [...prev, message];
        });
        if (message.senderRole === "Customer") {
          markAgentConversationRead(message.conversationId).catch(() => {});
        }
      }
      setList((prev) =>
        prev.map((row) =>
          row.id === message.conversationId
            ? {
                ...row,
                lastMessagePreview: message.content,
                lastMessageAt: message.sentAt,
                unreadCount:
                  message.senderRole === "Customer" &&
                  message.conversationId !== conversationIdRef.current
                    ? row.unreadCount + 1
                    : row.unreadCount,
              }
            : row
        )
      );
    },
    onQueued: (summary) => {
      loadDashboard();
      setList((prev) => (prev.some((r) => r.id === summary.id) ? prev : [summary, ...prev]));
    },
    onConversationUpdated: (summary) => {
      setList((prev) => prev.map((row) => (row.id === summary.id ? summary : row)));
      setConversation((prev) =>
        prev && prev.id === summary.id
          ? { ...prev, status: summary.status, priority: summary.priority }
          : prev
      );
    },
    onAssigned: (conversationId, agentId, agentName) => {
      setConversation((prev) =>
        prev && prev.id === conversationId
          ? { ...prev, agentId, agentName, status: "Active", isBotHandled: false }
          : prev
      );
      loadDashboard();
      if (agentId !== myUserId) {
        setList((prev) => prev.filter((r) => r.id !== conversationId));
      }
    },
    onClosed: (conversationId) => {
      setConversation((prev) =>
        prev && prev.id === conversationId ? { ...prev, status: "Closed" } : prev
      );
      // Kapalı sohbeti ana listeden kaldır
      setList((prev) => prev.filter((r) => r.id !== conversationId));
      loadDashboard();
    },
    onAgentPresence: () => loadDashboard(),
    onConnected: () => loadList(tab),
  });

  useEffect(() => {
    joinConversation(conversation?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id]);

  /* ── Eylemler ── */

  const changeAvailability = async (next: LiveChatAgentAvailability) => {
    setError("");
    try {
      await setAgentAvailability(next, maxChats);
      setAvailability(next);
      loadDashboard();
    } catch {
      setError("Müsaitlik güncellenemedi.");
    }
  };

  const handleClaim = async () => {
    if (!conversation) return;
    setError("");
    try {
      const res = await claimLiveChat(conversation.id);
      const normalized = normalizeConversation(res.data);
      setConversation(normalized);
      setMessages(normalized.messages);
      loadDashboard();
      loadList(tab);
    } catch {
      setError("Sohbet üstlenilemedi — başka bir temsilci almış olabilir.");
    }
  };

  const handleSend = async (text?: string) => {
    const content = (text ?? draft).trim();
    if (!content || !conversation || sending) return;

    const clientMessageId = newClientMessageId();
    const optimistic: ILiveChatMessage = {
      id: `pending-${clientMessageId}`,
      conversationId: conversation.id,
      senderId: myUserId,
      senderRole: "Agent",
      senderName: conversation.agentName ?? null,
      messageType: "Text",
      content,
      clientMessageId,
      sentAt: new Date().toISOString(),
      isRead: false,
    };

    setMessages((prev) => [...prev, optimistic]);
    const fromDraft = text === undefined;
    if (fromDraft) setDraft("");
    setSending(true);
    setError("");

    try {
      const res = await sendLiveChatMessage({ conversationId: conversation.id, content, clientMessageId });
      const saved = normalizeMessage(res.data);
      setMessages((prev) => prev.map((m) => (m.clientMessageId === clientMessageId ? saved : m)));
      if (!conversation.agentId) openConversation(conversation.id);
    } catch {
      setMessages((prev) => prev.filter((m) => m.clientMessageId !== clientMessageId));
      if (fromDraft) setDraft(content);
      setError("Mesaj gönderilemedi. Sohbet başka bir temsilcide olabilir.");
    } finally {
      setSending(false);
    }
  };

  const handleMacro = async (macro: ILiveChatQuickReply) => {
    setShowMacros(false);
    try {
      const res = await consumeQuickReply(macro.id);
      setDraft(res.data.content ?? macro.content);
    } catch {
      setDraft(macro.content);
    }
    textareaRef.current?.focus();
  };

  const handleResolve = async () => {
    if (!conversation) return;
    setError("");
    try {
      await resolveLiveChat({ conversationId: conversation.id, requestRating: true });
      setConversation((prev) => (prev ? { ...prev, status: "Resolved" } : prev));
      loadDashboard();
      loadList(tab);
    } catch {
      setError("Sohbet çözüldü olarak işaretlenemedi.");
    }
  };

  const handleClose = async () => {
    if (!conversation) return;
    setError("");
    try {
      await closeLiveChatAsAgent(conversation.id);
      setConversation((prev) => (prev ? { ...prev, status: "Closed" } : prev));
      loadDashboard();
      loadList(tab);
    } catch {
      setError("Sohbet kapatılamadı.");
    }
  };

  const isMine = conversation?.agentId === myUserId;
  const canWrite =
    !!conversation &&
    conversation.status !== "Closed" &&
    (isMine || !conversation.agentId);

  const AVAIL_CONFIG: Record<LiveChatAgentAvailability, { color: string; bg: string; dot: string }> = {
    Online:  { color: "#16a34a", bg: "#f0fdf4", dot: "#22c55e" },
    Away:    { color: "#d97706", bg: "#fffbeb", dot: "#f59e0b" },
    Offline: { color: "#6b7280", bg: "#f9fafb", dot: "#9ca3af" },
    Busy:    { color: "#dc2626", bg: "#fef2f2", dot: "#ef4444" },
  };

  /* ── MOBILE RETURN ──────────────────────────────────────── */
  if (isMobile) {
    const MobileClosedModal = showClosedModal ? (
      <>
        <div onClick={() => setShowClosedModal(false)} style={{ position: "fixed", inset: 0, zIndex: 1040, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(3px)" }} />
        <div style={{
          position: "fixed", top: "50%", left: "50%", zIndex: 1050,
          transform: "translate(-50%, -50%)",
          width: "calc(100vw - 24px)", maxHeight: "82dvh",
          background: "#fff", borderRadius: 20,
          boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}>
          <div style={{ padding: "16px 18px 14px", borderBottom: "1.5px solid #f1f5f9", display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <i className="bi bi-archive-fill" style={{ color: "#6b7280" }} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: "0.95rem", color: "#111827" }}>Kapalı Sohbetler</div>
              <div style={{ fontSize: "0.65rem", color: "#9ca3af" }}>
                {closedLoading ? "Yükleniyor…" : `${closedList.length} sohbet`}
              </div>
            </div>
            <button onClick={() => setShowClosedModal(false)} style={{ marginLeft: "auto", background: "#f1f5f9", border: "none", borderRadius: 8, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#6b7280" }}>
              <i className="bi bi-x-lg" />
            </button>
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            {closedLoading && <div style={{ display: "flex", justifyContent: "center", padding: "32px 0" }}><span className="spinner-border spinner-border-sm text-secondary" /></div>}
            {!closedLoading && closedList.length === 0 && (
              <div style={{ textAlign: "center", padding: "40px 20px", color: "#9ca3af" }}>
                <i className="bi bi-archive" style={{ fontSize: "2rem", display: "block", marginBottom: 8, opacity: 0.4 }} />
                <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>Kapalı sohbet yok</div>
              </div>
            )}
            {!closedLoading && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "12px" }}>
                {closedList.map((item) => {
                  const dateStr = item.lastMessageAt ? new Date(item.lastMessageAt).toLocaleDateString("tr-TR", { day: "numeric", month: "short" }) : "";
                  const timeStr = item.lastMessageAt ? new Date(item.lastMessageAt).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }) : "";
                  return (
                    <button key={item.id} onClick={() => { openConversation(item.id); setShowClosedModal(false); setMobileView("chat"); }}
                      className="lc-closed-card"
                      style={{ width: "100%", textAlign: "left", cursor: "pointer", border: "1.5px solid #e5e7eb", borderRadius: 12, background: "#fff", padding: "12px 14px", display: "flex", gap: 12, alignItems: "flex-start", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}
                    >
                      <div style={{ width: 40, height: 40, borderRadius: "50%", flexShrink: 0, background: "linear-gradient(135deg,#e0e7ff,#c7d2fe)", color: "#4f46e5", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.72rem", fontWeight: 800, border: "2px solid #c7d2fe" }}>
                        {getInitials(item.customerName)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
                          <span style={{ fontWeight: 800, fontSize: "0.88rem", color: "#111827" }}>{item.customerName || "Müşteri"}</span>
                          <div style={{ textAlign: "right", flexShrink: 0 }}>
                            <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#374151" }}>{dateStr}</div>
                            <div style={{ fontSize: "0.62rem", color: "#9ca3af" }}>{timeStr}</div>
                          </div>
                        </div>
                        <div style={{ fontSize: "0.76rem", color: "#6b7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 6, marginTop: 2 }}>
                          {item.lastMessagePreview || "—"}
                        </div>
                        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                          <span style={{ fontSize: "0.62rem", color: "#6366f1", fontWeight: 700, background: "#eef2ff", padding: "2px 7px", borderRadius: 10, border: "1px solid #c7d2fe" }}>#{item.conversationNo}</span>
                          <span style={{ fontSize: "0.62rem", color: "#6b7280", background: "#f1f5f9", padding: "2px 7px", borderRadius: 10 }}>{TOPIC_LABELS[item.topic]}</span>
                          <span style={{ fontSize: "0.62rem", fontWeight: 700, color: PRIORITY_COLORS[item.priority], background: `${PRIORITY_COLORS[item.priority]}18`, padding: "2px 7px", borderRadius: 10 }}>{PRIORITY_LABELS[item.priority]}</span>
                          {item.rating != null && <span style={{ fontSize: "0.62rem", fontWeight: 700, color: "#92400e", background: "#fef3c7", padding: "2px 7px", borderRadius: 10 }}><i className="bi bi-star-fill me-1" style={{ color: "#f59e0b" }} />{item.rating.toFixed(1)}</span>}
                        </div>
                      </div>
                      <i className="bi bi-chevron-right" style={{ color: "#c7d2fe", fontSize: "0.8rem", marginTop: 10, flexShrink: 0 }} />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {!closedLoading && closedList.length > 0 && (
            <div style={{ padding: "10px 16px", borderTop: "1px solid #f1f5f9", background: "#fafafa", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "0.7rem", color: "#9ca3af" }}>Son 50 kapalı sohbet</span>
              <button onClick={() => setShowClosedModal(false)} style={{ background: "#f1f5f9", border: "none", borderRadius: 8, padding: "6px 14px", fontWeight: 600, fontSize: "0.76rem", color: "#374151", cursor: "pointer" }}>Kapat</button>
            </div>
          )}
        </div>
      </>
    ) : null;

    return (
      <div style={{ height: "100dvh", display: "flex", flexDirection: "column", background: "#f6f8fb", overflow: "hidden" }}>

        {mobileView === "list" ? (
          /* ── MOBİL: Liste Ekranı ── */
          <>
            {/* Compact header */}
            <div style={{ background: "#fff", borderBottom: "1.5px solid #eef2ff", padding: "10px 14px 0", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 8 }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg, #6366f1, #4f46e5)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 3px 10px rgba(99,102,241,0.3)" }}>
                  <i className="bi bi-headset text-white" style={{ fontSize: "1rem" }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800, fontSize: "0.9rem", color: "#111827" }}>Canlı Destek</div>
                </div>
                <button onClick={() => setShowToolsModal(true)} title="Araçlar" style={{ background: "linear-gradient(135deg,#312e81,#4338ca)", border: "none", borderRadius: 9, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", boxShadow: "0 2px 8px rgba(99,102,241,0.35)" }}>
                  <i className="bi bi-tools" />
                </button>
                <button onClick={() => setShowOnCallModal(true)} title="Nöbetçiler" style={{ background: "linear-gradient(135deg,#064e3b,#065f46)", border: "none", borderRadius: 9, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", boxShadow: "0 2px 8px rgba(6,95,70,0.3)" }}>
                  <i className="bi bi-telephone-fill" />
                </button>
                <button onClick={openClosedModal} title="Kapalı sohbetler" style={{ background: "#f9fafb", border: "1.5px solid #e5e7eb", borderRadius: 9, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#6b7280" }}>
                  <i className="bi bi-archive" />
                </button>
                <button onClick={() => { loadDashboard(); loadList(tab); }} title="Yenile" style={{ background: "#f8faff", border: "1.5px solid #e0e7ff", borderRadius: 9, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#6366f1" }}>
                  <i className="bi bi-arrow-clockwise" />
                </button>
                {isLiveChatOnly && (
                  <button onClick={handleLogout} title="Çıkış Yap" style={{ background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: 9, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#dc2626" }}>
                    <i className="bi bi-box-arrow-right" />
                  </button>
                )}
              </div>
              {/* Müsaitlik */}
              <div style={{ display: "flex", gap: 6, paddingBottom: 10, overflowX: "auto" }}>
                {(["Online", "Away", "Offline"] as LiveChatAgentAvailability[]).map((a) => {
                  const cfg = AVAIL_CONFIG[a];
                  const isActive = availability === a;
                  return (
                    <button key={a} onClick={() => changeAvailability(a)} style={{ background: isActive ? cfg.bg : "transparent", border: `1.5px solid ${isActive ? cfg.dot : "#e5e7eb"}`, borderRadius: 20, padding: "5px 14px", fontSize: "0.75rem", fontWeight: isActive ? 700 : 500, color: isActive ? cfg.color : "#6b7280", cursor: "pointer", display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap", flexShrink: 0 }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: isActive ? cfg.dot : "#d1d5db", display: "inline-block" }} />
                      {AVAILABILITY_LABELS[a]}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Stat strip */}
            {dashboard && (
              <div style={{ background: "#fff", borderBottom: "1px solid #f1f5f9", display: "flex", gap: 8, padding: "8px 14px", overflowX: "auto", flexShrink: 0 }}>
                {[
                  { label: "Kuyrukta",    value: String(dashboard.waitingCount),   accent: dashboard.waitingCount > 0 },
                  { label: "Görüşülüyor", value: String(dashboard.activeCount) },
                  { label: "Asistanda",   value: String(dashboard.botHandledCount) },
                  { label: "24s Çözülen", value: String(dashboard.resolvedLast24h) },
                  { label: "İlk Yanıt",   value: formatSeconds(dashboard.averageFirstResponseSeconds) },
                  { label: "Çözüm",       value: formatSeconds(dashboard.averageResolutionSeconds) },
                  { label: "Puan",        value: dashboard.averageRating != null ? dashboard.averageRating.toFixed(1) : "—" },
                ].map(({ label, value, accent }) => (
                  <div key={label} style={{ flexShrink: 0, background: accent ? "linear-gradient(135deg,#6366f1,#4f46e5)" : "#f8faff", border: accent ? "none" : "1.5px solid #e0e7ff", borderRadius: 10, padding: "7px 13px", textAlign: "center", minWidth: 72 }}>
                    <div style={{ fontSize: "0.58rem", color: accent ? "rgba(255,255,255,0.8)" : "#9ca3af", fontWeight: 600 }}>{label}</div>
                    <div style={{ fontSize: "1.05rem", fontWeight: 800, color: accent ? "#fff" : "#111827", lineHeight: 1.1 }}>{value}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Error */}
            {error && (
              <div style={{ background: "#fef2f2", borderBottom: "1px solid #fecaca", padding: "8px 14px", fontSize: "0.78rem", color: "#b91c1c", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                <i className="bi bi-exclamation-circle-fill" />
                <span style={{ flex: 1 }}>{error}</span>
                <button onClick={() => setError("")} style={{ background: "none", border: "none", color: "#b91c1c", cursor: "pointer" }}><i className="bi bi-x-lg" /></button>
              </div>
            )}

            {/* Tab bar */}
            <div style={{ display: "flex", background: "#fff", borderBottom: "1.5px solid #eef2ff", flexShrink: 0 }}>
              {([["queue","Kuyruk","bi-inbox-fill"],["mine","Bendekiler","bi-person-fill"],["all","Tümü","bi-list-ul"]] as [ListTab,string,string][]).map(([key,label,icon]) => (
                <button key={key} onClick={() => setTab(key)} style={{ flex: 1, border: "none", borderBottom: tab === key ? "2.5px solid #6366f1" : "2.5px solid transparent", background: tab === key ? "#f5f3ff" : "transparent", padding: "11px 6px", cursor: "pointer", fontSize: "0.76rem", fontWeight: tab === key ? 700 : 500, color: tab === key ? "#6366f1" : "#6b7280", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                  <i className={`bi ${icon}`} />{label}
                </button>
              ))}
            </div>

            {/* Conversation list */}
            <div style={{ flex: 1, overflowY: "auto" }}>
              {listLoading && <div style={{ display: "flex", justifyContent: "center", padding: "32px 0" }}><span className="spinner-border spinner-border-sm text-primary" /></div>}
              {!listLoading && list.length === 0 && (
                <div style={{ textAlign: "center", padding: "48px 20px", color: "#9ca3af" }}>
                  <i className="bi bi-inbox" style={{ fontSize: "2.4rem", display: "block", marginBottom: 10, opacity: 0.4 }} />
                  <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>Sohbet yok</div>
                </div>
              )}
              {list.map((item) => (
                <ConversationRow
                  key={item.id}
                  item={item}
                  active={false}
                  onClick={() => { openConversation(item.id); setMobileView("chat"); }}
                />
              ))}
            </div>
          </>
        ) : (
          /* ── MOBİL: Sohbet Ekranı ── */
          <>
            {/* Chat header */}
            <div style={{ background: "#fff", borderBottom: "1.5px solid #eef2ff", padding: "10px 12px", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <button onClick={() => setMobileView("list")} style={{ background: "#f1f5f9", border: "none", borderRadius: 9, width: 38, height: 38, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#374151", flexShrink: 0 }}>
                <i className="bi bi-arrow-left" style={{ fontSize: "1rem" }} />
              </button>
              {conversation && (
                <>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0, background: "linear-gradient(135deg,#e0e7ff,#c7d2fe)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.72rem", fontWeight: 800, color: "#4f46e5" }}>
                    {getInitials(conversation.customerName)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: "0.88rem", color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {conversation.customerName || "Müşteri"}
                    </div>
                    <div style={{ fontSize: "0.6rem", color: "#9ca3af", display: "flex", gap: 4, alignItems: "center" }}>
                      <span style={{ color: "#6366f1", fontWeight: 700 }}>#{conversation.conversationNo}</span>
                      <span>·</span>
                      <span style={{ color: PRIORITY_COLORS[conversation.priority], fontWeight: 700 }}>{PRIORITY_LABELS[conversation.priority]}</span>
                      <span>·</span>
                      <span>{STATUS_LABELS[conversation.status]}</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                    {!conversation.agentId && conversation.status !== "Closed" && (
                      <button onClick={handleClaim} style={{ background: "linear-gradient(135deg,#6366f1,#4f46e5)", color: "#fff", border: "none", borderRadius: 8, padding: "6px 10px", fontWeight: 700, fontSize: "0.72rem", cursor: "pointer" }}>Üstlen</button>
                    )}
                    {isMine && conversation.status === "Active" && (
                      <button onClick={handleResolve} style={{ background: "linear-gradient(135deg,#22c55e,#16a34a)", color: "#fff", border: "none", borderRadius: 8, padding: "6px 10px", fontWeight: 700, fontSize: "0.72rem", cursor: "pointer" }}>Çözüldü</button>
                    )}
                    {isMine && conversation.status !== "Closed" && (
                      <button onClick={handleClose} style={{ background: "#fff", color: "#dc2626", border: "1.5px solid #fecaca", borderRadius: 8, padding: "6px 8px", fontWeight: 700, fontSize: "0.72rem", cursor: "pointer" }}>Kapat</button>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Locked banner */}
            {conversation?.agentId && !isMine && (
              <div style={{ padding: "7px 14px", background: "#fffbeb", borderBottom: "1px solid #fde68a", fontSize: "0.72rem", color: "#92400e", display: "flex", alignItems: "center", gap: 7, flexShrink: 0 }}>
                <i className="bi bi-lock-fill" style={{ color: "#f59e0b" }} />
                <strong>{conversation.agentName}</strong>&nbsp;üzerinde — yazmak için devir gerekir.
              </div>
            )}

            {/* Messages */}
            <div ref={threadRef} style={{ flex: 1, overflowY: "auto", padding: "12px 14px", background: "#f6f8fb", display: "flex", flexDirection: "column", gap: 10 }}>
              {threadLoading ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                  <span className="spinner-border spinner-border-sm text-primary" />
                </div>
              ) : (
                messages.map((m) => <Bubble key={m.id} message={m} myUserId={myUserId} onViewOrder={setOrderDetailNo} />)
              )}
            </div>

            {/* Error in chat */}
            {error && (
              <div style={{ background: "#fef2f2", borderTop: "1px solid #fecaca", padding: "7px 14px", fontSize: "0.76rem", color: "#b91c1c", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                <i className="bi bi-exclamation-circle-fill" />
                <span style={{ flex: 1 }}>{error}</span>
                <button onClick={() => setError("")} style={{ background: "none", border: "none", color: "#b91c1c", cursor: "pointer" }}><i className="bi bi-x-lg" /></button>
              </div>
            )}

            {/* Input bar */}
            {canWrite ? (
              <div style={{ background: "#fff", borderTop: "2px solid #eef2ff", flexShrink: 0 }}>
                {showMacros && macros.length > 0 && (
                  <div style={{ borderBottom: "1px solid #eef2ff", maxHeight: 180, overflowY: "auto" }}>
                    <div style={{ padding: "8px 14px 6px", fontSize: "0.68rem", fontWeight: 700, color: "#6366f1", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between" }}>
                      <span><i className="bi bi-lightning-charge-fill me-1" />Hazır Yanıtlar</span>
                      <button onClick={() => setShowMacros(false)} style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer" }}><i className="bi bi-x" /></button>
                    </div>
                    {macros.map((macro) => (
                      <button key={macro.id} onClick={() => handleMacro(macro)} className="lc-macro-btn" style={{ display: "block", width: "100%", textAlign: "left", border: "none", borderBottom: "1px solid #f8faff", background: "transparent", padding: "10px 14px", cursor: "pointer" }}>
                        <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#111827" }}>{macro.title}</div>
                        <div style={{ fontSize: "0.72rem", color: "#9ca3af", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{macro.content}</div>
                      </button>
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", alignItems: "flex-end", gap: 8, padding: "10px 12px" }}>
                  <button onClick={() => setShowMacros((s) => !s)} style={{ background: showMacros ? "#eef2ff" : "#f8faff", border: `1.5px solid ${showMacros ? "#c7d2fe" : "#e0e7ff"}`, borderRadius: 10, width: 42, height: 42, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: showMacros ? "#4f46e5" : "#9ca3af", fontSize: "1rem" }}>
                    <i className="bi bi-lightning-charge" />
                  </button>
                  <div className="lc-input-box" style={{ flex: 1, background: "#f8faff", border: "2px solid #e0e7ff", borderRadius: 14, padding: "8px 12px" }}>
                    <textarea
                      ref={textareaRef}
                      rows={1}
                      maxLength={2000}
                      value={draft}
                      placeholder="Yanıtınızı yazın…"
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                      className="lc-textarea"
                      style={{ border: "none", background: "transparent", resize: "none", outline: "none", fontSize: "0.92rem", color: "#111827", lineHeight: 1.5, width: "100%", padding: 0, fontFamily: "inherit", maxHeight: 100 }}
                    />
                  </div>
                  <button onClick={() => handleSend()} disabled={sending || draft.trim().length === 0} style={{ background: draft.trim() ? "linear-gradient(135deg,#6366f1,#4f46e5)" : "#e5e7eb", color: draft.trim() ? "#fff" : "#9ca3af", border: "none", borderRadius: 12, width: 46, height: 46, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", cursor: draft.trim() && !sending ? "pointer" : "default", fontSize: "1.1rem", boxShadow: draft.trim() ? "0 3px 12px rgba(99,102,241,0.3)" : "none", transition: "all 0.15s" }}>
                    {sending ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-send-fill" />}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ borderTop: "2px solid #eef2ff", padding: "14px 16px", textAlign: "center", color: "#9ca3af", fontSize: "0.78rem", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, flexShrink: 0, background: "#fff" }}>
                {conversation?.status === "Closed"
                  ? <><i className="bi bi-lock-fill" style={{ color: "#d1d5db" }} /> Sohbet kapandı</>
                  : <><i className="bi bi-info-circle" style={{ color: "#a5b4fc" }} /> Yazmak için sohbeti üstlenmeniz gerekir</>}
              </div>
            )}
          </>
        )}

        {/* Kapalı sohbetler modal */}
        {MobileClosedModal}

        {/* Sipariş detay modal */}
        {orderDetailNo && (
          <OrderDetailModal orderNo={orderDetailNo} onClose={() => setOrderDetailNo(null)} />
        )}

        {/* Araçlar modal */}
        {showToolsModal && <AgentToolsModal onClose={() => setShowToolsModal(false)} />}

        {/* Nöbetçiler modal */}
        {showOnCallModal && <OnCallAgentModal myUserId={myUserId} onClose={() => setShowOnCallModal(false)} />}
      </div>
    );
  }

  /* ── DESKTOP RETURN (değiştirilmedi) ─────────────────────── */
  return (
    <div className="container-fluid py-3" style={{ maxWidth: 1600 }}>

      {/* ── Üst bar ── */}
      <div style={{
        background: "#fff", border: "1.5px solid #eef2ff", borderRadius: 16,
        padding: "14px 20px", marginBottom: 16,
        display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12,
            background: "linear-gradient(135deg, #6366f1, #4f46e5)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 12px rgba(99,102,241,0.3)",
          }}>
            <i className="bi bi-headset text-white" style={{ fontSize: "1.2rem" }} />
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: "1rem", color: "#111827", lineHeight: 1.1 }}>Canlı Destek</div>
            <div style={{ fontSize: "0.68rem", color: "#9ca3af" }}>Temsilci Paneli</div>
          </div>
        </div>

        {/* Müsaitlik seçici */}
        <div style={{ display: "flex", gap: 6, marginLeft: 4 }}>
          {(["Online", "Away", "Offline"] as LiveChatAgentAvailability[]).map((a) => {
            const cfg = AVAIL_CONFIG[a];
            const isActive = availability === a;
            return (
              <button
                key={a}
                onClick={() => changeAvailability(a)}
                style={{
                  background: isActive ? cfg.bg : "transparent",
                  border: `1.5px solid ${isActive ? cfg.dot : "#e5e7eb"}`,
                  borderRadius: 20, padding: "4px 12px",
                  fontSize: "0.76rem", fontWeight: isActive ? 700 : 500,
                  color: isActive ? cfg.color : "#6b7280",
                  cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
                  transition: "all 0.15s",
                }}
              >
                <span style={{
                  width: 7, height: 7, borderRadius: "50%",
                  background: isActive ? cfg.dot : "#d1d5db",
                  display: "inline-block",
                }} />
                {AVAILABILITY_LABELS[a]}
              </button>
            );
          })}
        </div>

        {/* Eşzamanlı */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: "0.72rem", color: "#6b7280", fontWeight: 600 }}>Eşzamanlı:</span>
          <input
            type="number" min={1} max={30} value={maxChats}
            onChange={(e) => setMaxChats(Number(e.target.value))}
            onBlur={() => changeAvailability(availability)}
            style={{
              width: 56, borderRadius: 8, border: "1.5px solid #e5e7eb",
              padding: "3px 8px", fontSize: "0.82rem", fontWeight: 700,
              textAlign: "center", color: "#111827", outline: "none",
            }}
          />
        </div>

        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button
            onClick={() => setShowToolsModal(true)}
            style={{
              background: "linear-gradient(135deg, #312e81, #4338ca)",
              border: "none", borderRadius: 8, padding: "5px 14px",
              fontWeight: 700, fontSize: "0.78rem",
              color: "#fff", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
              boxShadow: "0 2px 10px rgba(99,102,241,0.35)",
            }}
          >
            <i className="bi bi-tools" />
            Araçlar
          </button>
          <button
            onClick={() => setShowOnCallModal(true)}
            style={{
              background: "linear-gradient(135deg, #064e3b, #065f46)",
              border: "none", borderRadius: 8, padding: "5px 14px",
              fontWeight: 700, fontSize: "0.78rem",
              color: "#fff", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
              boxShadow: "0 2px 10px rgba(6,95,70,0.3)",
            }}
          >
            <i className="bi bi-telephone-fill" />
            Nöbetçiler
          </button>
          <button
            onClick={openClosedModal}
            style={{
              background: "#f9fafb", border: "1.5px solid #e5e7eb",
              borderRadius: 8, padding: "5px 12px",
              fontWeight: 600, fontSize: "0.78rem",
              color: "#6b7280", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            <i className="bi bi-archive" />
            Kapalı Sohbetler
          </button>
          <button
            onClick={() => { loadDashboard(); loadList(tab); }}
            style={{
              background: "#f8faff", border: "1.5px solid #e0e7ff",
              borderRadius: 8, padding: "5px 12px",
              fontWeight: 600, fontSize: "0.78rem",
              color: "#6366f1", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 5,
            }}
          >
            <i className="bi bi-arrow-clockwise" />Yenile
          </button>
          {isLiveChatOnly && (
            <button
              onClick={handleLogout}
              style={{
                background: "#fef2f2", border: "1.5px solid #fecaca",
                borderRadius: 8, padding: "5px 12px",
                fontWeight: 700, fontSize: "0.78rem",
                color: "#dc2626", cursor: "pointer",
                display: "flex", alignItems: "center", gap: 5,
              }}
            >
              <i className="bi bi-box-arrow-right" />Çıkış Yap
            </button>
          )}
        </div>
      </div>

      {/* ── İstatistik kartları ── */}
      {dashboard && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
          <StatCard label="Kuyrukta" value={String(dashboard.waitingCount)}
            hint={`En uzun ${dashboard.longestWaitingMinutes} dk`} accent={dashboard.waitingCount > 0} />
          <StatCard label="Görüşülüyor" value={String(dashboard.activeCount)} />
          <StatCard label="Asistanda"   value={String(dashboard.botHandledCount)} />
          <StatCard label="24s Çözülen" value={String(dashboard.resolvedLast24h)} />
          <StatCard label="İlk Yanıt"   value={formatSeconds(dashboard.averageFirstResponseSeconds)} />
          <StatCard label="Çözüm"       value={formatSeconds(dashboard.averageResolutionSeconds)} />
          <StatCard label="Puan"
            value={dashboard.averageRating != null ? dashboard.averageRating.toFixed(1) : "—"} />
          <StatCard label="Bot Çözme"
            value={`%${Math.round((dashboard.botDeflectionRate ?? 0) * 100)}`} />
        </div>
      )}

      {/* ── Hata bandı ── */}
      {error && (
        <div style={{
          background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: 10,
          padding: "8px 14px", marginBottom: 14,
          display: "flex", alignItems: "center", gap: 8, fontSize: "0.82rem", color: "#b91c1c",
        }}>
          <i className="bi bi-exclamation-circle-fill" />
          <span style={{ flex: 1 }}>{error}</span>
          <button onClick={() => setError("")} style={{ background: "none", border: "none", color: "#b91c1c", cursor: "pointer", padding: 0 }}>
            <i className="bi bi-x-lg" />
          </button>
        </div>
      )}

      {/* ── İki kolon ── */}
      <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 14, alignItems: "start" }}>

        {/* ── Sol: sohbet listesi ── */}
        <div style={{ border: "1.5px solid #eef2ff", borderRadius: 14, overflow: "hidden", background: "#fff" }}>
          {/* Tab bar */}
          <div style={{ display: "flex", borderBottom: "1.5px solid #eef2ff" }}>
            {([
              ["queue", "Kuyruk",    "bi-inbox-fill"],
              ["mine",  "Bendekiler","bi-person-fill"],
              ["all",   "Tümü",      "bi-list-ul"],
            ] as [ListTab, string, string][]).map(([key, label, icon]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                style={{
                  flex: 1, border: "none", borderBottom: tab === key ? "2.5px solid #6366f1" : "2.5px solid transparent",
                  background: tab === key ? "#f5f3ff" : "transparent",
                  padding: "10px 6px", cursor: "pointer",
                  fontSize: "0.76rem", fontWeight: tab === key ? 700 : 500,
                  color: tab === key ? "#6366f1" : "#6b7280",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                  transition: "all 0.15s",
                }}
              >
                <i className={`bi ${icon}`} />
                {label}
              </button>
            ))}
          </div>

          {/* Liste */}
          <div style={{ maxHeight: "calc(100vh - 330px)", minHeight: 300, overflowY: "auto" }}>
            {listLoading && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 0" }}>
                <span className="spinner-border spinner-border-sm text-primary" />
              </div>
            )}
            {!listLoading && list.length === 0 && (
              <div style={{ textAlign: "center", padding: "40px 20px", color: "#9ca3af" }}>
                <i className="bi bi-inbox" style={{ fontSize: "2rem", display: "block", marginBottom: 8 }} />
                <div style={{ fontSize: "0.8rem" }}>Sohbet yok</div>
              </div>
            )}
            {list.map((item) => (
              <ConversationRow
                key={item.id}
                item={item}
                active={conversation?.id === item.id}
                onClick={() => openConversation(item.id)}
              />
            ))}
          </div>
        </div>

        {/* ── Sağ: sohbet paneli ── */}
        <div style={{
          border: "1.5px solid #eef2ff", borderRadius: 14, overflow: "hidden",
          display: "flex", flexDirection: "column",
          height: "calc(100vh - 280px)", minHeight: 500, background: "#fff",
        }}>
          {!conversation ? (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#9ca3af" }}>
              <div style={{
                width: 72, height: 72, borderRadius: 20,
                background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center",
                marginBottom: 12,
              }}>
                <i className="bi bi-chat-dots" style={{ fontSize: "2rem", color: "#c7d2fe" }} />
              </div>
              <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#374151" }}>Bir sohbet seçin</div>
              <div style={{ fontSize: "0.78rem", marginTop: 4 }}>Soldaki listeden görüşme başlatın</div>
            </div>
          ) : (
            <>
              {/* Sohbet başlığı */}
              <div style={{
                padding: "12px 18px", borderBottom: "1.5px solid #eef2ff",
                display: "flex", alignItems: "center", gap: 12, background: "#fff",
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
                  background: "linear-gradient(135deg, #e0e7ff, #c7d2fe)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 800, fontSize: "0.78rem", color: "#4f46e5",
                }}>
                  {getInitials(conversation.customerName)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: "0.95rem", color: "#111827" }}>
                    {conversation.customerName || "Müşteri"}
                  </div>
                  <div style={{ fontSize: "0.66rem", color: "#9ca3af", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ color: "#6366f1", fontWeight: 700 }}>#{conversation.conversationNo}</span>
                    <span>·</span>
                    <span>{TOPIC_LABELS[conversation.topic]}</span>
                    <span>·</span>
                    <span style={{ color: PRIORITY_COLORS[conversation.priority], fontWeight: 700 }}>
                      {PRIORITY_LABELS[conversation.priority]}
                    </span>
                    <span>·</span>
                    <span>{STATUS_LABELS[conversation.status]}</span>
                    {conversation.agentName && <><span>·</span><span>{conversation.agentName}</span></>}
                  </div>
                </div>

                {/* Eylem butonları */}
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  {!conversation.agentId && conversation.status !== "Closed" && (
                    <button onClick={handleClaim} style={{
                      background: "linear-gradient(135deg, #6366f1, #4f46e5)", color: "#fff",
                      border: "none", borderRadius: 8, padding: "6px 12px",
                      fontWeight: 700, fontSize: "0.76rem", cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 5,
                      boxShadow: "0 3px 10px rgba(99,102,241,0.3)",
                    }}>
                      <i className="bi bi-hand-index-thumb" /> Üstlen
                    </button>
                  )}
                  {isMine && conversation.status === "Active" && (
                    <button onClick={handleResolve} style={{
                      background: "linear-gradient(135deg, #22c55e, #16a34a)", color: "#fff",
                      border: "none", borderRadius: 8, padding: "6px 12px",
                      fontWeight: 700, fontSize: "0.76rem", cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 5,
                      boxShadow: "0 3px 10px rgba(34,197,94,0.3)",
                    }}>
                      <i className="bi bi-check2-circle" /> Çözüldü
                    </button>
                  )}
                  {isMine && conversation.status !== "Closed" && (
                    <button onClick={handleClose} style={{
                      background: "#fff", color: "#dc2626",
                      border: "1.5px solid #fecaca", borderRadius: 8, padding: "6px 12px",
                      fontWeight: 700, fontSize: "0.76rem", cursor: "pointer",
                    }}>
                      Kapat
                    </button>
                  )}
                </div>
              </div>

              {/* Başka temsilcide uyarısı */}
              {conversation.agentId && !isMine && (
                <div style={{
                  padding: "7px 18px", background: "#fffbeb",
                  borderBottom: "1px solid #fde68a",
                  fontSize: "0.74rem", color: "#92400e",
                  display: "flex", alignItems: "center", gap: 7,
                }}>
                  <i className="bi bi-lock-fill" style={{ color: "#f59e0b" }} />
                  Bu sohbet <strong style={{ marginLeft: 3 }}>{conversation.agentName}</strong>'de — yazmak için devir gerekir.
                </div>
              )}

              {/* Mesajlar */}
              <div
                ref={threadRef}
                style={{
                  flex: 1, overflowY: "auto",
                  padding: "16px 18px",
                  background: "#f6f8fb",
                  display: "flex", flexDirection: "column", gap: 10,
                }}
              >
                {threadLoading ? (
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                    <span className="spinner-border spinner-border-sm text-primary" />
                  </div>
                ) : (
                  messages.map((m) => <Bubble key={m.id} message={m} myUserId={myUserId} onViewOrder={setOrderDetailNo} />)
                )}
              </div>

              {/* Input alanı */}
              {canWrite ? (
                <div style={{ background: "#fff", borderTop: "2px solid #eef2ff", padding: "12px 14px" }}>
                  {/* Hazır yanıtlar paneli */}
                  {showMacros && macros.length > 0 && (
                    <div style={{
                      background: "#fff", border: "1.5px solid #e0e7ff", borderRadius: 12,
                      marginBottom: 10, maxHeight: 190, overflowY: "auto",
                      boxShadow: "0 -4px 20px rgba(99,102,241,0.1)",
                    }}>
                      <div style={{
                        padding: "8px 12px 6px",
                        borderBottom: "1px solid #eef2ff",
                        fontSize: "0.68rem", fontWeight: 700, color: "#6366f1",
                        display: "flex", alignItems: "center", gap: 5,
                      }}>
                        <i className="bi bi-lightning-charge-fill" /> Hazır Yanıtlar
                        <button
                          onClick={() => setShowMacros(false)}
                          style={{ marginLeft: "auto", background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: "0.8rem", padding: 0 }}
                        >
                          <i className="bi bi-x" />
                        </button>
                      </div>
                      {macros.map((macro) => (
                        <button
                          key={macro.id}
                          onClick={() => handleMacro(macro)}
                          className="lc-macro-btn"
                          style={{
                            display: "block", width: "100%", textAlign: "left",
                            border: "none", borderBottom: "1px solid #f8faff",
                            background: "transparent", padding: "8px 14px", cursor: "pointer",
                          }}
                        >
                          <div style={{ fontSize: "0.76rem", fontWeight: 700, color: "#111827" }}>{macro.title}</div>
                          <div style={{ fontSize: "0.68rem", color: "#9ca3af", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {macro.content}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Yazışma kutusu */}
                  <div className="lc-input-box" style={{
                    background: "#f8faff",
                    border: "2px solid #e0e7ff",
                    borderRadius: 14,
                    padding: "10px 12px 8px",
                    display: "flex", flexDirection: "column", gap: 8,
                  }}>
                    <textarea
                      ref={textareaRef}
                      rows={3}
                      maxLength={2000}
                      value={draft}
                      placeholder="Yanıtınızı yazın…  (Enter gönderir, Shift+Enter yeni satır)"
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                      className="lc-textarea"
                      style={{
                        border: "none", background: "transparent",
                        resize: "none", outline: "none",
                        fontSize: "0.88rem", color: "#111827",
                        lineHeight: 1.5, width: "100%", padding: 0,
                        fontFamily: "inherit",
                      }}
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          onClick={() => setShowMacros((s) => !s)}
                          title="Hazır yanıtlar"
                          style={{
                            background: showMacros ? "#eef2ff" : "transparent",
                            border: `1.5px solid ${showMacros ? "#c7d2fe" : "#e0e7ff"}`,
                            borderRadius: 8, padding: "4px 9px",
                            color: showMacros ? "#4f46e5" : "#6b7280",
                            cursor: "pointer", fontSize: "0.82rem",
                            display: "flex", alignItems: "center", gap: 4,
                            transition: "all 0.15s",
                          }}
                        >
                          <i className="bi bi-lightning-charge" />
                          {macros.length > 0 && (
                            <span style={{ fontSize: "0.64rem", fontWeight: 700 }}>{macros.length}</span>
                          )}
                        </button>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: "0.64rem", color: "#9ca3af" }}>{draft.length}/2000</span>
                        <button
                          onClick={() => handleSend()}
                          disabled={sending || draft.trim().length === 0}
                          style={{
                            background: draft.trim() ? "linear-gradient(135deg, #6366f1, #4f46e5)" : "#e5e7eb",
                            color: draft.trim() ? "#fff" : "#9ca3af",
                            border: "none", borderRadius: 10, padding: "7px 18px",
                            fontWeight: 700, fontSize: "0.82rem",
                            cursor: draft.trim() && !sending ? "pointer" : "default",
                            display: "flex", alignItems: "center", gap: 6,
                            boxShadow: draft.trim() ? "0 3px 12px rgba(99,102,241,0.3)" : "none",
                            transition: "all 0.15s",
                          }}
                        >
                          {sending ? (
                            <span className="spinner-border spinner-border-sm" />
                          ) : (
                            <><i className="bi bi-send-fill" /> Gönder</>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{
                  borderTop: "2px solid #eef2ff", padding: "14px",
                  textAlign: "center", color: "#9ca3af", fontSize: "0.78rem",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                }}>
                  {conversation.status === "Closed" ? (
                    <><i className="bi bi-lock-fill" style={{ color: "#d1d5db" }} /> Sohbet kapandı</>
                  ) : (
                    <><i className="bi bi-info-circle" style={{ color: "#a5b4fc" }} /> Yazmak için sohbeti üstlenmeniz gerekir</>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Kapalı Sohbetler Modal ── */}
      {showClosedModal && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setShowClosedModal(false)}
            style={{
              position: "fixed", inset: 0, zIndex: 1040,
              background: "rgba(0,0,0,0.45)",
              backdropFilter: "blur(3px)",
            }}
          />
          {/* Modal */}
          <div style={{
            position: "fixed", top: "50%", left: "50%", zIndex: 1050,
            transform: "translate(-50%, -50%)",
            width: "min(680px, 95vw)",
            maxHeight: "80vh",
            background: "#fff",
            borderRadius: 20,
            boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
            display: "flex", flexDirection: "column",
            overflow: "hidden",
          }}>
            {/* Modal başlık */}
            <div style={{
              padding: "18px 22px 16px",
              borderBottom: "1.5px solid #f1f5f9",
              display: "flex", alignItems: "center", gap: 12,
            }}>
              <div style={{
                width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                background: "#f1f5f9",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <i className="bi bi-archive-fill" style={{ fontSize: "1.1rem", color: "#6b7280" }} />
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: "1rem", color: "#111827" }}>
                  Kapalı Sohbetler
                </div>
                <div style={{ fontSize: "0.68rem", color: "#9ca3af" }}>
                  {closedLoading ? "Yükleniyor…" : `${closedList.length} sohbet`}
                </div>
              </div>
              <button
                onClick={() => setShowClosedModal(false)}
                style={{
                  marginLeft: "auto", background: "#f1f5f9", border: "none",
                  borderRadius: 8, width: 32, height: 32,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", color: "#6b7280", fontSize: "0.9rem",
                }}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>

            {/* Liste */}
            <div style={{ overflowY: "auto", flex: 1 }}>
              {closedLoading && (
                <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
                  <span className="spinner-border spinner-border-sm text-secondary" />
                </div>
              )}

              {!closedLoading && closedList.length === 0 && (
                <div style={{ textAlign: "center", padding: "48px 20px", color: "#9ca3af" }}>
                  <i className="bi bi-archive" style={{ fontSize: "2.2rem", display: "block", marginBottom: 10, opacity: 0.4 }} />
                  <div style={{ fontWeight: 600, fontSize: "0.88rem" }}>Kapalı sohbet yok</div>
                  <div style={{ fontSize: "0.76rem", marginTop: 4 }}>Henüz kapanmış görüşme bulunmuyor.</div>
                </div>
              )}

              {!closedLoading && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "14px 16px" }}>
                  {closedList.map((item) => {
                    const initials = getInitials(item.customerName);
                    const dateStr = item.lastMessageAt
                      ? new Date(item.lastMessageAt).toLocaleDateString("tr-TR", {
                          day: "numeric", month: "short",
                        })
                      : "";
                    const timeStr = item.lastMessageAt
                      ? new Date(item.lastMessageAt).toLocaleTimeString("tr-TR", {
                          hour: "2-digit", minute: "2-digit",
                        })
                      : "";

                    return (
                      <button
                        key={item.id}
                        onClick={() => {
                          openConversation(item.id);
                          setShowClosedModal(false);
                        }}
                        className="lc-closed-card"
                        style={{
                          width: "100%", textAlign: "left", cursor: "pointer",
                          border: "1.5px solid #e5e7eb", borderRadius: 14,
                          background: "#fff", padding: "14px 16px",
                          display: "flex", gap: 14, alignItems: "flex-start",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                          transition: "all 0.15s",
                        }}
                      >
                        {/* Avatar */}
                        <div style={{
                          width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
                          background: "linear-gradient(135deg, #e0e7ff, #c7d2fe)",
                          color: "#4f46e5",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: "0.8rem", fontWeight: 800,
                          border: "2px solid #c7d2fe",
                        }}>
                          {initials}
                        </div>

                        {/* İçerik */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {/* Üst satır: isim + zaman */}
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 4 }}>
                            <div style={{ minWidth: 0 }}>
                              <span style={{ fontWeight: 800, fontSize: "0.92rem", color: "#111827" }}>
                                {item.customerName || "Müşteri"}
                              </span>
                              {item.agentName && (
                                <span style={{
                                  marginLeft: 8, fontSize: "0.7rem", color: "#6366f1",
                                  background: "#eef2ff", padding: "1px 8px", borderRadius: 10,
                                  fontWeight: 600,
                                }}>
                                  <i className="bi bi-person-check me-1" />{item.agentName}
                                </span>
                              )}
                            </div>
                            {(dateStr || timeStr) && (
                              <div style={{
                                display: "flex", flexDirection: "column", alignItems: "flex-end",
                                flexShrink: 0, gap: 2,
                              }}>
                                <span style={{ fontSize: "0.7rem", fontWeight: 700, color: "#374151" }}>{dateStr}</span>
                                <span style={{ fontSize: "0.65rem", color: "#9ca3af" }}>{timeStr}</span>
                              </div>
                            )}
                          </div>

                          {/* Son mesaj */}
                          <div style={{
                            fontSize: "0.8rem", color: "#6b7280",
                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                            marginBottom: 8,
                            background: "#f8faff", borderRadius: 8, padding: "5px 10px",
                            border: "1px solid #f1f5f9",
                          }}>
                            <i className="bi bi-chat-left-text me-1" style={{ color: "#c7d2fe" }} />
                            {item.lastMessagePreview || "—"}
                          </div>

                          {/* Alt etiketler */}
                          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                            <span style={{
                              fontSize: "0.65rem", color: "#6366f1", fontWeight: 700,
                              background: "#eef2ff", padding: "2px 9px", borderRadius: 20,
                              border: "1px solid #c7d2fe",
                            }}>
                              #{item.conversationNo}
                            </span>
                            <span style={{
                              fontSize: "0.65rem", color: "#6b7280", fontWeight: 600,
                              background: "#f1f5f9", padding: "2px 9px", borderRadius: 20,
                            }}>
                              {TOPIC_LABELS[item.topic]}
                            </span>
                            <span style={{
                              fontSize: "0.65rem", fontWeight: 700,
                              color: PRIORITY_COLORS[item.priority],
                              background: `${PRIORITY_COLORS[item.priority]}18`,
                              padding: "2px 9px", borderRadius: 20,
                            }}>
                              {PRIORITY_LABELS[item.priority]}
                            </span>
                            {item.rating != null && (
                              <span style={{
                                fontSize: "0.65rem", color: "#92400e", fontWeight: 700,
                                background: "#fef3c7", padding: "2px 9px", borderRadius: 20,
                                display: "flex", alignItems: "center", gap: 3,
                              }}>
                                <i className="bi bi-star-fill" style={{ color: "#f59e0b" }} />
                                {item.rating.toFixed(1)}
                              </span>
                            )}
                            <span style={{
                              fontSize: "0.65rem", color: "#6b7280", fontWeight: 600,
                              background: "#f1f5f9", padding: "2px 9px", borderRadius: 20,
                              marginLeft: "auto",
                              display: "flex", alignItems: "center", gap: 4,
                            }}>
                              <i className="bi bi-archive" style={{ fontSize: "0.6rem" }} /> Kapandı
                            </span>
                          </div>
                        </div>

                        <i className="bi bi-chevron-right" style={{ color: "#c7d2fe", fontSize: "0.85rem", marginTop: 12, flexShrink: 0 }} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {!closedLoading && closedList.length > 0 && (
              <div style={{
                padding: "12px 20px", borderTop: "1px solid #f1f5f9",
                background: "#fafafa",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <span style={{ fontSize: "0.72rem", color: "#9ca3af" }}>
                  Son 50 kapalı sohbet gösteriliyor
                </span>
                <button
                  onClick={() => setShowClosedModal(false)}
                  style={{
                    background: "#f1f5f9", border: "none", borderRadius: 8,
                    padding: "6px 14px", fontWeight: 600, fontSize: "0.78rem",
                    color: "#374151", cursor: "pointer",
                  }}
                >
                  Kapat
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Sipariş detay modal */}
      {orderDetailNo && (
        <OrderDetailModal orderNo={orderDetailNo} onClose={() => setOrderDetailNo(null)} />
      )}

      {/* Araçlar modal */}
      {showToolsModal && <AgentToolsModal onClose={() => setShowToolsModal(false)} />}

      {/* Nöbetçiler modal */}
      {showOnCallModal && <OnCallAgentModal myUserId={myUserId} onClose={() => setShowOnCallModal(false)} />}
    </div>
  );
}

export default LiveChatAgentPage;
